import { useState } from 'react';
import { BlockStack, Button, Text, Banner } from '@shopify/polaris';
import { useApi } from '../providers/ApiProvider';
import { showAppToast } from './ContextualSaveBar';
import { t } from '../i18n';

export type ThemeWidgetColors = {
  accentColor?: string;
  backgroundColor?: string;
  textColor?: string;
  mutedColor?: string;
  buttonTextColor?: string;
  selectedBorder?: string;
  giftCardBg?: string;
  giftCardBorder?: string;
  overlayColor?: string;
  lineColor?: string;
  schemeId?: string;
};

/**
 * Shown under Auto-fit on widget editors only (not campaigns).
 * Pulls live MAIN theme colors and applies them to the open design.
 */
export function ThemeColorRefresh({
  onApply,
}: {
  onApply: (colors: ThemeWidgetColors) => void;
}) {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    setNote(null);
    try {
      const res = await api.get<{
        source: string;
        themeName?: string | null;
        schemeId?: string | null;
        colors: ThemeWidgetColors | null;
        message?: string;
      }>('/stores/theme/colors');
      if (!res.colors) {
        setError(
          res.message ||
            'Could not load theme colors. Publish your theme and try again.',
        );
        return;
      }
      onApply({
        ...res.colors,
        schemeId: res.colors.schemeId || res.schemeId || undefined,
      });
      const schemeNote = res.schemeId ? ` · ${res.schemeId}` : '';
      const msg =
        res.message ||
        `Colors refreshed from “${res.themeName || 'your theme'}”${schemeNote}.`;
      setNote(msg);
      showAppToast(
        res.schemeId
          ? `Theme colors refreshed (${res.schemeId})`
          : 'Theme colors refreshed',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to refresh theme colors');
    } finally {
      setLoading(false);
    }
  }

  return (
    <BlockStack gap="200">
      <Text as="p" tone="subdued" variant="bodySm">
        {t("After you publish a new theme or change theme colors, click Refresh so\n        this widget picks up the latest scheme.")}
      </Text>
      <div>
        <Button onClick={() => void refresh()} loading={loading}>
          {t("Refresh theme colors")}
        </Button>
      </div>
      {note && (
        <Banner tone="success" onDismiss={() => setNote(null)}>
          <p>{note}</p>
        </Banner>
      )}
      {error && (
        <Banner tone="critical" onDismiss={() => setError(null)}>
          <p>{error}</p>
        </Banner>
      )}
    </BlockStack>
  );
}
